import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Grid3x3, List, Search, SlidersHorizontal, ArrowRight } from "lucide-react";
import { useCategories, useProducts, productUrl } from "@/lib/api";
import { FINISHES } from "@/lib/calc";
import { FinishChips, PageHero, ProductCard, Reveal } from "@/components/site/common";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { breadcrumbLd, useSeo } from "@/lib/seo";

export function ProductsLanding() {
  const { data: cats = [] } = useCategories();
  const [q, setQ] = useState("");
  const nav = useNavigate();
  useSeo("Products", "Fence fittings, construction stakes, foundation bolts, threaded rods, washers and cut rebars — the full Tijiya range.");
  return (
    <div data-testid="products-landing">
      <PageHero eyebrow="Products" title="Steel hardware, made to specification" sub="Three families of products, 46 items, each available in multiple sizes and finishes — and made to drawing when needed.">
        <form onSubmit={(e) => { e.preventDefault(); nav(`/products/all?q=${encodeURIComponent(q)}`); }} className="mt-8 flex max-w-lg">
          <input data-testid="products-search-input" value={q} onChange={(e) => setQ(e.target.value)} className="field" placeholder="Search products, e.g. tension band" />
          <button data-testid="products-search-submit" className="btn btn-brass"><Search size={16} /></button>
        </form>
        <Link to="/products/all" data-testid="products-all-link" className="mt-5 inline-block link text-sm">All Products A–Z →</Link>
      </PageHero>
      <section className="wrap py-20 space-y-6">
        {cats.map((c, i) => (
          <Reveal key={c.slug} delay={i * 80}>
            <Link to={`/products/${c.slug}`} data-testid={`landing-category-${c.slug}`} className="group card grid md:grid-cols-12 hover:border-[var(--brass)] transition-colors duration-300">
              <div className="md:col-span-4 img-frame prod-bg aspect-[4/3] md:aspect-auto flex items-center justify-center p-10 border-b md:border-b-0 md:border-r border-[var(--stone)]"><img src={c.image} alt="" className="max-h-48 object-contain" /></div>
              <div className="md:col-span-8 p-8 lg:p-12 flex flex-col justify-center">
                <p className="sec-num">0{i + 1} · {c.count} products</p>
                <h2 className="text-3xl lg:text-4xl mt-2">{c.name}</h2>
                <p className="mt-3 text-[var(--graphite)] max-w-xl">{c.tagline}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--brass)]">View range <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
    </div>
  );
}

const MATERIALS = ["Mild Steel"];
const APPLICATIONS = ["Chain Link Fence", "Security Fence", "Concrete Formwork", "Foundation", "Landscaping"];

function Filters({ f, setF, prods }) {
  const toggle = (k, v) => setF((cur) => ({ ...cur, [k]: cur[k].includes(v) ? cur[k].filter((x) => x !== v) : [...cur[k], v] }));
  const has = (arr) => arr.filter((o) => o);
  const groups = [["finish", "Finish", FINISHES.filter((o) => prods.some((p) => p.finishes.includes(o)))], ["material", "Material", has(MATERIALS.filter((o) => prods.some((p) => p.material === o)))], ["application", "Application", APPLICATIONS.filter((o) => prods.some((p) => p.applications.includes(o)))]].filter((g) => g[2].length);
  return (
    <div className="space-y-8">
      {groups.map(([k, l, opts]) => (
        <div key={k}>
          <p className="label">{l}</p>
          <div className="space-y-2 mt-3">
            {opts.map((o) => (
              <label key={o} className="flex items-center gap-3 text-sm cursor-pointer">
                <input type="checkbox" data-testid={`filter-${k}-${o.replace(/\s+/g, "-").toLowerCase()}`} checked={f[k].includes(o)} onChange={() => toggle(k, o)} className="accent-[var(--brass)] w-4 h-4" /> {o}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button data-testid="filters-clear" className="text-xs link" onClick={() => setF({ finish: [], material: [], application: [] })}>Clear filters</button>
    </div>
  );
}

export function Category() {
  const { category } = useParams();
  const { data: cats = [] } = useCategories();
  const { data: prods = [], isLoading } = useProducts({ category });
  const [f, setF] = useState({ finish: [], material: [], application: [] });
  const [drawer, setDrawer] = useState(false);
  const c = cats.find((x) => x.slug === category);
  const list = prods.filter((p) => (!f.finish.length || f.finish.some((x) => p.finishes.includes(x))) && (!f.material.length || f.material.includes(p.material)) && (!f.application.length || f.application.some((x) => p.applications.includes(x))));
  useSeo(c?.name, c?.tagline, c && breadcrumbLd([["Home", "/"], ["Products", "/products"], [c.name, `/products/${c.slug}`]]));
  if (cats.length && !c) return <div className="wrap py-32"><h1 className="text-4xl">Category not found</h1><Link to="/products" className="link mt-4 inline-block">Back to products</Link></div>;
  return (
    <div data-testid="category-page">
      {c && (
        <section className="border-b border-[var(--stone)]">
          <div className="wrap py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs text-[var(--graphite)] mb-4"><Link to="/" className="hover:text-[var(--brass)]">Home</Link> / <Link to="/products" className="hover:text-[var(--brass)]">Products</Link> / {c.name}</p>
              <h1 data-testid="category-title" className="text-4xl sm:text-5xl lg:text-6xl">{c.name}</h1>
              {c.description.split("\n\n").map((t) => <p key={t.slice(0, 20)} className="mt-5 text-[var(--graphite)] md:text-lg max-w-2xl">{t}</p>)}
              <div className="mt-6 flex flex-wrap gap-2">{c.applications.map((a) => <span key={a} className="chip">{a}</span>)}</div>
            </div>
            <div className="lg:col-span-5 img-frame prod-bg aspect-[4/3] border border-[var(--stone)] flex items-center justify-center p-10"><img src={c.image} alt="" className="max-h-full object-contain" /></div>
          </div>
        </section>
      )}
      <section className="wrap py-14 grid lg:grid-cols-12 gap-10">
        <aside className="hidden lg:block lg:col-span-3"><div className="sticky top-28"><Filters f={f} setF={setF} prods={prods} /></div></aside>
        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-6">
            <p data-testid="category-count" className="text-sm text-[var(--graphite)] tabular">{list.length} products</p>
            <button data-testid="filters-open-mobile" className="lg:hidden btn btn-outline btn-sm" onClick={() => setDrawer(true)}><SlidersHorizontal size={14} /> Filters</button>
          </div>
          {isLoading ? <p className="text-sm">Loading…</p> : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">{list.map((p) => <ProductCard key={p.slug} p={p} />)}</div>
          )}
          {!isLoading && list.length === 0 && <p className="text-sm text-[var(--graphite)]">No products match these filters.</p>}
        </div>
      </section>
      <section className="wrap pb-20">
        <div className="card p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <p className="serif text-3xl">Can't find your size? We manufacture to drawing.</p>
          <Link to="/enquiry" data-testid="category-drawing-cta" className="btn btn-brass">Send your drawing</Link>
        </div>
      </section>
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="bg-[var(--paper)]"><SheetHeader><SheetTitle className="serif text-2xl font-medium">Filters</SheetTitle></SheetHeader><div className="mt-6"><Filters f={f} setF={setF} prods={prods} /></div></SheetContent>
      </Sheet>
    </div>
  );
}

export function AllProducts() {
  const params = new URLSearchParams(window.location.search);
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState("all");
  const [view, setView] = useState("grid");
  const { data: cats = [] } = useCategories();
  const { data: prods = [] } = useProducts();
  useSeo("All Products A–Z", "Alphabetical index of every Tijiya product.");
  const groups = useMemo(() => {
    const list = prods.filter((p) => (cat === "all" || p.category === cat) && p.name.toLowerCase().includes(q.toLowerCase())).sort((a, b) => a.name.replace(/^'/, "").localeCompare(b.name.replace(/^'/, "")));
    const g = {};
    list.forEach((p) => { const l = p.name.replace(/^'/, "")[0].toUpperCase(); (g[l] = g[l] || []).push(p); });
    return g;
  }, [prods, q, cat]);
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  return (
    <div data-testid="all-products-page">
      <section className="wrap pt-16 pb-8 border-b border-[var(--stone)]">
        <p className="eyebrow mb-4">Index</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl">All Products A–Z</h1>
        <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="relative flex-1 max-w-md"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--steel)]" /><input data-testid="az-search" value={q} onChange={(e) => setQ(e.target.value)} className="field !pl-9" placeholder="Instant search…" /></div>
          <div className="flex flex-wrap gap-2">
            {[["all", "All"], ...cats.map((c) => [c.slug, c.name.split(" ")[0] + " " + (c.name.split(" ")[1] || "")])].map(([k, l]) => <button key={k} data-testid={`az-tab-${k}`} data-active={cat === k} onClick={() => setCat(k)} className="chip chip-select">{l.replace(/,$/, "")}</button>)}
          </div>
          <div className="flex gap-1 md:ml-auto">
            <button aria-label="Grid view" data-testid="az-view-grid" data-active={view === "grid"} onClick={() => setView("grid")} className="chip chip-select"><Grid3x3 size={14} /></button>
            <button aria-label="List view" data-testid="az-view-list" data-active={view === "list"} onClick={() => setView("list")} className="chip chip-select"><List size={14} /></button>
          </div>
        </div>
        <nav className="mt-6 flex flex-wrap gap-1 text-sm" aria-label="Letters">
          {letters.map((l) => groups[l] ? <a key={l} href={`#letter-${l}`} data-testid={`az-letter-${l}`} className="w-8 h-8 flex items-center justify-center hover:bg-[var(--brass)] hover:text-[var(--paper)] transition-colors">{l}</a> : <span key={l} className="w-8 h-8 flex items-center justify-center text-[var(--stone)]">{l}</span>)}
        </nav>
      </section>
      <section className="wrap py-12 space-y-12">
        {Object.keys(groups).length === 0 && <p data-testid="az-empty" className="text-sm text-[var(--graphite)]">No products match “{q}”.</p>}
        {Object.entries(groups).map(([l, ps]) => (
          <div key={l} id={`letter-${l}`} className="scroll-mt-28">
            <p className="serif text-5xl text-[var(--brass)] border-b border-[var(--stone)] pb-3 mb-6">{l}</p>
            {view === "grid" ? <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{ps.map((p) => <ProductCard key={p.slug} p={p} />)}</div> : (
              <div className="divide-y divide-[var(--stone)]">{ps.map((p) => (
                <Link key={p.slug} to={productUrl(p)} data-testid={`az-row-${p.slug}`} className="flex items-center gap-5 py-3 hover:bg-[var(--paper)] transition-colors">
                  <img src={p.images[0]} alt="" className="w-12 h-12 object-contain prod-bg" loading="lazy" />
                  <span className="serif text-xl flex-1">{p.name}</span>
                  <span className="hidden md:block text-xs text-[var(--graphite)]">{cats.find((c) => c.slug === p.category)?.name}</span>
                  <FinishChips finishes={p.finishes} />
                </Link>
              ))}</div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
